/**
 * The MEASUREMENT SHEET (ADR 0045) — a printable record of the EMPTY room.
 *
 * This is the document a designer takes to site: the plan, a front elevation of
 * every wall that carries something, schedules locating each obstruction and
 * ceiling opening, and chained field dimensions on the elevations.
 * It deliberately shows **nothing about the design** (no sections, no storage) —
 * it captures the space before anything is built in it (owner, 2026-07-29).
 *
 * WHY IT REUSES THE LIVE RENDERERS
 * Both drawings come from `createCanvas`/`createElevation` bound to hidden,
 * print-only SVGs. They are factories, so a second instance is free, and it
 * means the sheet cannot drift from what the designer was just looking at —
 * a second drawing implementation would be a second set of bugs.
 *
 * WHY IT PRINTS RATHER THAN GENERATES A PDF
 * `window.print()` → "Save as PDF" needs no dependency, works offline, and keeps
 * the drawings as VECTORS. A bundled PDF engine would rasterise them, which is
 * the one thing a measurement drawing cannot afford.
 *
 * WHAT IS DELIBERATELY IGNORED
 * The Elev toggle and the grid toggle are VIEW preferences; a document has its
 * own rules. Elevations appear for every wall with an obstruction or referenced
 * ceiling opening whether or not it is ticked on screen, and the 6" grid is
 * always on — it is the sheet's scale reference, since pages are fit-to-page
 * rather than a fixed ratio.
 */
import { createCanvas } from './canvas.js';
import { createElevation } from './elevation.js';

/** Gap between a fixture's top edge and its label's baseline, in user units. */
const LABEL_GAP_UNITS = 3;

/**
 * What a page calls itself when the caller does not say.
 *
 * The sheet is no longer the only document built from these pages — the
 * remeasure report (D96) renders the same empty-room plan and elevations
 * against a different dataset. A page stamped "Measurement sheet" while showing
 * remeasured numbers is a drawing lying about which dataset it is, so the
 * caller names the document and this is only the default.
 */
export const DEFAULT_DOCUMENT_LABEL = 'Measurement sheet';

/**
 * The sheet records the EMPTY space, so every design-side feed is stubbed out
 * (owner, 2026-07-29). Overriding the option feeds — rather than filtering the
 * model — means the sketch itself is untouched and a design added tomorrow
 * cannot leak onto the sheet through a renderer nobody remembered to update.
 *
 * Exported so the tests can assert the stub really blanks every design feed —
 * the interior-elevation work (ADR 0055) made "interiors never leak onto the
 * sheet" an explicit acceptance point, and this object is the mechanism.
 */
export const EMPTY_SPACE_FEEDS = Object.freeze({
  sectionLayoutFor: () => null,
  sectionsFor: () => [],
  stacksFor: () => [],
  // Composed interiors (ADR 0055) ride the stacks' units, so an empty stacks
  // feed already starves them — this stub is defense in depth, per the note
  // above: no future caller of interiorFor may find one here either.
  interiorFor: () => null,
  lShelfLegsFor: () => [],
  connectingShelvesFor: () => [],
  // Cabinetry on the NEXT wall is still cabinetry: the sheet records the
  // empty space, so the abutting run's end panel goes with the rest.
  abuttingPanelsFor: () => [],
  // Standard section-type labels (DH/LH/…) are the design's shorthand.
  wallLabelsFor: () => [],
  wallLabels: [],
  // Depth lines are cabinet depths, not features of the room.
  helperLinesFor: () => [],
});

/**
 * The middle of the top edge of an SVG `points` list — the plan's fixtures are
 * bare polygons, and a polygon has no x/y/width to read.
 *
 * Exported for its own tests: this is the one piece of real arithmetic in the
 * labelling, and the format is loose — `points` legally separates numbers with
 * commas, spaces, newlines or any mix of them, so a naive split gets it wrong.
 * "Top" is the SMALLEST y, since SVG's y axis grows downward.
 *
 * @param {string} points e.g. "10,20 30,20 30,40 10,40"
 * @returns {{x:number, y:number}|null} null if it cannot be read
 */
export function polygonTopCentre(points) {
  const nums = String(points ?? '').trim().split(/[\s,]+/).map(Number);
  if (nums.length < 2 || !nums.every(Number.isFinite)) return null;
  const xs = nums.filter((_, i) => i % 2 === 0);
  const ys = nums.filter((_, i) => i % 2 === 1);
  if (!xs.length || !ys.length) return null;
  return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: Math.min(...ys) };
}

/**
 * @param {Document} root
 * @param {object} deps { model, rules, fmt, wallRenderOpts, elevationOptsFor }
 */
export function createMeasurementSheet(root, deps) {
  let model = deps.model;
  let activeDeps = deps;
  const { fmt } = deps;
  const overlay = root.getElementById('sheet-overlay');
  const pagesBox = root.getElementById('sheet-pages');
  if (!overlay || !pagesBox) {
    return { open: () => {}, close: () => {}, renderPages: () => {} };
  }

  // Where `page()` appends, and what each page header stamps. Both are swapped
  // for the duration of a render and restored afterwards, the same way `model`
  // and `activeDeps` already are — see `renderPages`.
  let pagesTarget = pagesBox;
  let documentLabel = DEFAULT_DOCUMENT_LABEL;

  const emptySpaceOnly = EMPTY_SPACE_FEEDS;

  const el = (tag, cls, text) => {
    const node = root.createElement(tag);
    if (cls) node.className = cls;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const svgEl = (cls) => {
    const node = root.createElementNS('http://www.w3.org/2000/svg', 'svg');
    node.setAttribute('class', cls);
    return node;
  };

  /** Client · Opportunity · Space · Version, repeated on every page. */
  function pageHeader(title, meta) {
    const head = el('header', 'sheet-page__head');
    const left = el('div', 'sheet-page__id');
    left.append(el('h1', 'sheet-page__title', meta.identity || 'Room sketch'));
    left.append(el('p', 'sheet-page__sub', title));
    const right = el('div', 'sheet-page__meta');
    right.append(el('p', 'sheet-page__sub', meta.dateLabel));
    right.append(el('p', 'sheet-page__sub', documentLabel));
    head.append(left, right);
    return head;
  }

  function page() {
    const p = el('section', 'sheet-page');
    pagesTarget.append(p);
    return p;
  }

  /**
   * Paper scale for a drawing, stated so the page is self-explaining.
   *
   * Pages are fit-to-page, so every page can land on a different ratio — the
   * figure is derived from the view the renderer settled on rather than assumed.
   * The 6" grid carries the same information visually; this is the number for
   * anyone who wants it.
   */
  function scaleNote(svg, view, pxPerUnit, boxWidthInches) {
    const vb = svg.viewBox?.baseVal;
    if (!vb || !vb.width || !(pxPerUnit > 0) || !(view?.scale > 0)) return '6″ grid';
    const paperInchesPerUnit = (boxWidthInches / vb.width) * view.scale;
    const realInchesPerPaperInch = 1 / (paperInchesPerUnit * pxPerUnit);
    if (!Number.isFinite(realInchesPerPaperInch) || realInchesPerPaperInch <= 0) return '6″ grid';
    return `6″ grid · 1″ on paper ≈ ${fmt(realInchesPerPaperInch)}`;
  }

  /**
   * Writes each obstruction's schedule ID onto the drawing, beside the item.
   *
   * This is what makes the document navigable: a row in the table can be found
   * on the plan AND on that wall's elevation. The interactive views draw no such
   * labels on purpose — at screen size they swamped the fixture they named
   * (owner, 2026-07-24) — but on paper, at one wall per page, the mapping IS the
   * point of the sheet.
   */
  function labelObstructions(svg, rows, { view = null } = {}) {
    const byModelId = new Map(rows.map((o) => [o.obstructionId, o.id]));
    for (const node of svg.querySelectorAll('[data-obstruction-id]')) {
      const sheetId = byModelId.get(node.getAttribute('data-obstruction-id'));
      if (!sheetId) continue;
      const anchor = topCentreOf(node);
      if (!anchor) continue;
      const tag = root.createElementNS('http://www.w3.org/2000/svg', 'text');
      // PLAN geometry lives inside the canvas viewport transform, so a label
      // appended beside it inherits the fit-to-page zoom and inflates with a
      // small room (owner, 2026-08-28: "way too big — should be the same as
      // Wall indicators"). With `view`, project into the ROOT svg and append
      // there — constant paper size, exactly how the wall chips are placed
      // (and how labelCeilingOpenings already does it).
      let parent = node.parentNode;
      let { x, y } = anchor;
      if (view) {
        x = x * view.scale + view.x;
        y = y * view.scale + view.y;
        parent = svg;
      }
      tag.setAttribute('x', String(x));
      if (anchor.centred) {
        // The plan states exactly where the label goes — out in the room, off
        // the fixture's inner face — so sit ON that point rather than above it.
        tag.setAttribute('y', String(y));
        tag.setAttribute('dominant-baseline', 'central');
      } else {
        // The baseline sits above the fixture's top edge, so the label never
        // covers the thing it names.
        tag.setAttribute('y', String(y - LABEL_GAP_UNITS));
      }
      tag.setAttribute('text-anchor', 'middle');
      tag.setAttribute('class', 'sheet-obstruction-id');
      tag.textContent = sheetId;
      parent.append(tag);
    }
  }

  /** Adds the plan/elevation code that ties each ceiling opening to its row. */
  function labelCeilingOpenings(svg, rows, { elevation = false, view = null } = {}) {
    const byModelId = new Map(rows.map((opening) => [opening.openingId, opening.id]));
    const selector = elevation
      ? '.elev-ceiling-opening[data-ceiling-opening-id]'
      : '.ceiling-opening[data-ceiling-opening-id]';
    for (const node of svg.querySelectorAll(selector)) {
      const sheetId = byModelId.get(node.getAttribute('data-ceiling-opening-id'));
      if (!sheetId) continue;
      let x; let y; let parent = node.parentNode;
      if (elevation) {
        const width = Number(node.getAttribute('width'));
        const height = Number(node.getAttribute('height'));
        x = Number(node.getAttribute('x')) + width / 2;
        y = Number(node.getAttribute('y')) + Math.max(height, 1) + 15;
      } else {
        const poly = node.querySelector?.('polygon');
        const anchor = poly ? polygonTopCentre(poly.getAttribute('points')) : null;
        if (!anchor) continue;
        // Plan geometry lives inside the canvas viewport transform. Put this
        // sheet-only code directly in the root SVG after applying that view so
        // its type remains a constant paper size instead of scaling with room.
        x = view ? anchor.x * view.scale + view.x : anchor.x;
        y = view ? anchor.y * view.scale + view.y - LABEL_GAP_UNITS : anchor.y - LABEL_GAP_UNITS;
        if (view) parent = svg;
      }
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      const tag = root.createElementNS('http://www.w3.org/2000/svg', 'text');
      tag.setAttribute('x', String(x));
      tag.setAttribute('y', String(y));
      tag.setAttribute('text-anchor', 'middle');
      tag.setAttribute('class', 'sheet-ceiling-opening-id');
      tag.textContent = sheetId;
      parent.append(tag);
    }
  }

  /**
   * Where a fixture's label goes: the middle of its top edge, in the SVG's own
   * user units, read from the shape's OWN attributes.
   *
   * Deliberately NOT `getBBox()` (owner, 2026-08-05: obstruction codes missing
   * from the sheet's elevations). A sheet page is assembled detached and only
   * appended to the document afterwards, and a detached SVG has no layout — so
   * `getBBox()` returned an empty rect, every label was written to (0, −3), and
   * they stacked in the corner. On the plan that put them at the very edge; on
   * an elevation, y = −3 is outside the viewBox entirely, so they vanished.
   * The old finite-check could not catch it, because 0 is a perfectly finite
   * number.
   *
   * Reading the attributes needs no layout, so it works detached, in print, and
   * in a test.
   *
   * Elevation fixtures are a `<g>` wrapping a `<rect>`; plan fixtures are a
   * bare `<polygon>`.
   */
  function topCentreOf(node) {
    // The PLAN states its own anchor: a footprint's "top" on screen is only
    // toward the room for a wall that happens to face that way, so a label
    // placed there lands half on the wall band — black on black (owner,
    // 2026-08-05). The canvas knows the inward normal and hands us a point out
    // in the room, which is right at any wall angle.
    const lx = Number(node.getAttribute?.('data-label-x'));
    const ly = Number(node.getAttribute?.('data-label-y'));
    if (Number.isFinite(lx) && Number.isFinite(ly)) return { x: lx, y: ly, centred: true };

    const rect = node.tagName === 'rect' ? node : node.querySelector?.('rect');
    if (rect) {
      const x = Number(rect.getAttribute('x'));
      const y = Number(rect.getAttribute('y'));
      const w = Number(rect.getAttribute('width'));
      if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(w)) {
        return { x: x + w / 2, y };
      }
    }
    const poly = node.tagName === 'polygon' ? node : node.querySelector?.('polygon');
    return poly ? polygonTopCentre(poly.getAttribute('points')) : null;
  }

  /** The plan page: every wall, with the ones carrying obstructions marked. */
  function planPage(meta, sheet) {
    const p = page();
    p.append(pageHeader('Plan', meta));
    const frame = el('div', `sheet-page__drawing${sheet.ceilingOpenings.length ? ' sheet-page__drawing--plan-scheduled' : ''}`);
    const svg = svgEl('sheet-svg');
    svg.setAttribute('viewBox', '0 0 1000 620');
    frame.append(svg);
    p.append(frame);

    const canvas = createCanvas(svg, {});
    canvas.renderWalls(model.state.walls, null, (w) => model.displayLength(w), {
      ...activeDeps.wallRenderOpts(),
      ...emptySpaceOnly,
      gridVisible: true,
    });
    canvas.fit(model.allPoints());

    const withItems = sheet.walls.filter((w) => w.obstructions.length > 0 || w.ceilingOpenings.length > 0);
    // The plan carries the same IDs as the elevations — the owner's "clear
    // indicator of which walls contain obstructions", and it doubles as the map
    // between the two drawings.
    labelObstructions(svg, sheet.walls.flatMap((w) => w.obstructions), { view: canvas.getView() });
    labelCeilingOpenings(svg, sheet.ceilingOpenings, { view: canvas.getView() });

    if (sheet.ceilingOpenings.length) p.append(ceilingOpeningTable(sheet.ceilingOpenings));

    const footer = el('footer', 'sheet-page__foot');
    footer.append(el('span', 'sheet-legend', scaleNote(svg, canvas.getView(), model.state.pxPerUnit, 10)));
    footer.append(el('span', 'sheet-legend', withItems.length
      ? `Walls with measured items (elevations follow): ${withItems.map((w) => w.label).join(', ')}`
      : 'No wall or ceiling items recorded.'));
    p.append(footer);
    return withItems;
  }

  /** One page per wall that carries something: its elevation and its schedule. */
  function wallPage(meta, wallRow) {
    const wall = model.state.walls.find((w) => w.id === wallRow.wallId);
    if (!wall) return;
    const p = page();
    p.append(pageHeader(`${wallRow.label} — front elevation · ${fmt(wallRow.lengthUnits)} wide`, meta));

    const frame = el('div', 'sheet-page__drawing sheet-page__drawing--elev');
    const svg = svgEl('sheet-svg');
    const thumb = svgEl('sheet-svg sheet-svg--thumb');
    frame.append(svg);
    p.append(frame);

    const elevation = createElevation(svg, thumb, {});
    const scheduledById = new Map(wallRow.obstructions.map((item) => [item.obstructionId, item]));
    const scheduledCeilingById = new Map(wallRow.ceilingOpenings.map((item) => [item.openingId, item]));
    // The Elev toggle is a view preference; the document shows the wall either
    // way, so it is forced on for this copy of the segment.
    elevation.render([{ ...wall, includeInElevation: true }], {
      ...activeDeps.elevationOptsFor(),
      ...emptySpaceOnly,
      // Cabinet datums (panel tops, countertop, bottom shelf) describe the
      // DESIGN, so they go with the sections.
      referenceLines: [],
      gridVisible: true,
      selectedId: null,
      measurementForObstruction: (id) => scheduledById.get(id) ?? null,
      measurementForCeilingOpening: (id) => scheduledCeilingById.get(id) ?? null,
    });

    // No obstruction labelling pass here: since 2026-08-05 the elevation draws each
    // fixture's CODE itself, and it is the same code this table is keyed on.
    // Labelling again would print it twice.
    labelCeilingOpenings(svg, wallRow.ceilingOpenings, { elevation: true });

    if (wallRow.obstructions.length) p.append(scheduleTable(wallRow));
    if (wallRow.ceilingOpenings.length) {
      p.append(el('p', 'sheet-related-openings',
        `Room obstruction${wallRow.ceilingOpenings.length === 1 ? '' : 's'} on this elevation: ${wallRow.ceilingOpenings.map((opening) => opening.id).join(', ')} · wall-edge and vertical dimensions are shown here; exact two-wall locations are scheduled on the plan page.`));
    }
    const footer = el('footer', 'sheet-page__foot');
    footer.append(el('span', 'sheet-legend', '6″ grid · FL = left end to item · FR = item to right end · FF = from finished floor'));
    p.append(footer);
  }

  /** One plan-level row per ceiling opening, located from both reference walls. */
  function ceilingOpeningTable(openings) {
    const table = el('table', 'sheet-table sheet-table--ceiling-openings');
    const caption = root.createElement('caption');
    caption.textContent = 'Room obstructions';
    table.append(caption);
    const thead = root.createElement('thead');
    const head = root.createElement('tr');
    for (const h of ['ID', 'Item', 'Reference 1 · inward', 'Reference 2 · inward', 'Size', 'Vertical', 'Stair floor base']) {
      head.append(el('th', null, h));
    }
    thead.append(head);
    table.append(thead);
    const tbody = root.createElement('tbody');
    for (const opening of openings) {
      const tr = root.createElement('tr');
      tr.append(el('td', 'sheet-table__id', opening.id));
      tr.append(el('td', null, opening.type));
      const fan = opening.kind === 'ceiling_fan';
      tr.append(el('td', null, `${opening.reference1} · ${fan ? 'center ' : ''}${fmt(opening.fromReference1Units)}`));
      tr.append(el('td', null, `${opening.reference2} · ${fan ? 'center ' : ''}${fmt(opening.fromReference2Units)}`));
      tr.append(el('td', null, fan
        ? `Ø ${fmt(opening.diameterUnits ?? opening.widthUnits)}`
        : `${fmt(opening.widthUnits)} × ${fmt(opening.lengthUnits)}`));
      tr.append(el('td', null, fan
        ? `FF ${fmt(opening.fromFloorUnits)}`
        : `Below ceiling ${fmt(opening.heightUnits)}`));
      tr.append(el('td', null, opening.ladderFloorOffsetUnits == null
        ? '—' : `${opening.reference2} · ${fmt(opening.ladderFloorOffsetUnits)}`));
      tbody.append(tr);
    }
    table.append(tbody);
    return table;
  }

  function scheduleTable(wallRow) {
    const table = el('table', 'sheet-table');
    // A real <thead>, so that when a long schedule runs onto a second sheet the
    // browser repeats the column headings on it. A bare header <tr> does not
    // repeat, which leaves the continuation page as five unlabelled columns of
    // numbers — worse than useless on a job site.
    const thead = root.createElement('thead');
    const head = root.createElement('tr');
    for (const h of ['ID', 'Item', 'Size (W × H)', 'From left (FL)', 'From right (FR)', 'From floor']) {
      head.append(el('th', null, h));
    }
    thead.append(head);
    table.append(thead);
    const tbody = root.createElement('tbody');
    table.append(tbody);
    for (const o of wallRow.obstructions) {
      const tr = root.createElement('tr');
      tr.append(el('td', 'sheet-table__id', o.id));
      tr.append(el('td', null, o.type));
      tr.append(el('td', null, `${fmt(o.widthUnits)} × ${fmt(o.heightUnits)}`));
      tr.append(el('td', null, `FL ${fmt(o.fromLeftUnits)}`));
      tr.append(el('td', null, `FR ${fmt(o.fromRightUnits)}`));
      tr.append(el('td', null, `FF ${fmt(o.fromFloorUnits)}`));
      tbody.append(tr);
    }
    return table;
  }

  /**
   * Builds the pages for every selected space into `box`, and puts every piece
   * of swapped state back afterwards.
   *
   * Separate from `open` so a SECOND document can be assembled from the same
   * pages without a second full-screen layer. `.sheet-overlay` is
   * `position: fixed; inset: 0; z-index: 90`, and `#remeasure-overlay` carries
   * that same class later in the document (index.html:254 against :244) — two
   * peers tie on z and the later one wins, so a sheet opened from the remeasure
   * report would have rendered underneath it, entirely covered. That is the
   * RS-5/RS-6 defect for a third time. The remeasure report therefore renders
   * these pages into its OWN container, where nothing has to outrank anything.
   *
   * `meta.documentLabel` names the document on every page. Per-room meta still
   * supplies the identity line; the label is a property of the whole document,
   * so it is read once from the document meta rather than per room.
   */
  function renderPages(box, meta, rooms = []) {
    if (!box) return;
    box.replaceChildren();
    const originalModel = model;
    const originalDeps = activeDeps;
    const originalTarget = pagesTarget;
    const originalLabel = documentLabel;
    pagesTarget = box;
    documentLabel = meta?.documentLabel ?? DEFAULT_DOCUMENT_LABEL;
    try {
      // The no-rooms fallback must carry the CONSTRUCTOR's option feeds, not
      // just the model. `{ model, meta }` alone has no `wallRenderOpts`, and
      // `activeDeps = room` then replaced the working deps with an object that
      // has none — so `open(meta)` with no rooms threw in planPage and produced
      // an empty overlay. Reachable today: `open-sheet` builds its room list by
      // filtering out templates, so opening the sheet on a template sketch
      // passes zero rooms. Found while extracting `renderPages`; it predates it.
      const sources = rooms.length ? rooms : [{ ...deps, model, meta }];
      for (const room of sources) {
        model = room.model;
        activeDeps = room;
        const roomMeta = room.meta ?? meta;
        const sheet = model.measurementSheet();
        const firstPage = box.childElementCount;
        const withItems = planPage(roomMeta, sheet);
        for (const wallRow of withItems) wallPage(roomMeta, wallRow);

        if (sheet.partitionObstructionCount > 0) {
          const note = el('p', 'sheet-warning',
            `${sheet.partitionObstructionCount} obstruction(s) on partition faces are NOT listed on this sheet — partition numbering is not settled yet. Measure them by hand.`);
          box.children[firstPage]?.append(note);
        }
      }
    } finally {
      // `finally`, not a plain trailing restore: a throw inside one room's
      // render would otherwise leave the app bound to that room's model and
      // drawing the wrong space from then on, with nothing to point at.
      model = originalModel;
      activeDeps = originalDeps;
      pagesTarget = originalTarget;
      documentLabel = originalLabel;
    }
  }

  /** Rebuilds the document from every selected space, and shows the overlay. */
  function open(meta, rooms = []) {
    renderPages(pagesBox, meta, rooms);
    overlay.hidden = false;
  }

  const close = () => { overlay.hidden = true; pagesBox.replaceChildren(); };
  return { open, close, renderPages };
}
